// @ts-strict-ignore
import * as React from 'react';
import { RootState } from '../reducers/reducers';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import {
    AppDistributionService,
    ReleaseStepRollbackBuildData,
    ReleaseStatus,
    ReleaseTimelinePhase,
    ResignSequenceProgressState,
    RollbackResigningStrategy,
    AutomationType,
} from '../types';
import styles from './styles/stepStyles';
import Text from 'antd/lib/typography/Text';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Select from 'antd/lib/select';
import { antGrey } from './styles/globalStyles';
import { ReleaseStepInactiveContext } from '../contexts';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import {
    selectRelease,
    selectApp,
    selectAllAppIdentifiers,
    selectAppStoreIntegration,
    selectIsRunwayOrg,
    selectStabilityIntegrations,
    selectRollbackTargetVersionInfo,
    selectReleaseStep,
    selectAutomationById,
    selectAppDistributionServices,
    selectStepRun,
    selectSigningKeys,
} from '../selectors/selectors';
import RollbackBuildModule from './workflowModule/RollbackBuildModule';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { PastReleaseComponent } from './appHome/PastReleaseComponent';
import {
    automationIsActive,
    getAutomationIdByType,
    getError,
    getIsLoading,
    getUpdated,
    innerStepStatusColorForReleaseStepStatus,
} from '../helpers';
import { tryUpdateRelease } from '../actions/release';
import { ReleaseUpdatable } from '../actions/updatable';
import { globalMessage } from '../utils/globalMessage';
import { RunwayUserAction } from '../helpers/aclHelpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import ReleaseActionsComponent from './sharedComponents/ReleaseActionsComponent';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { StepRunUpdatable } from '@/actions/stepRun';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { RunwayResponseType } from '@/types';

class StepContentRollbackBuild extends React.Component<Props> {
    constructor(props) {
        super(props);
    }

    titleForResignState = (resignState: ResignSequenceProgressState, strategy: RollbackResigningStrategy) => {
        switch (resignState) {
            case ResignSequenceProgressState.NotStarted:
                return `Ready to ${strategy === RollbackResigningStrategy.CIResign ? 'rebuild' : 're-sign'}`;
            case ResignSequenceProgressState.Completed:
                return 'Success';
            case ResignSequenceProgressState.Failed:
                return 'Failed';
            case ResignSequenceProgressState.InProgress:
                return 'In progress';
        }
    };

    render() {
        return (
            <Layout>
                <Content
                    style={{
                        ...styles.innerContent,
                        paddingTop: 0,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'stretch',
                            marginBottom: 24,
                        }}
                    >
                        <div
                            style={{ display: 'flex', flexShrink: 0, gap: 16, flexDirection: 'column', paddingTop: 28 }}
                        >
                            <Space direction="vertical" size={4}>
                                <Text
                                    style={{
                                        ...styles.stepContentStatusText,
                                        color: innerStepStatusColorForReleaseStepStatus(this.props.stepStatus),
                                    }}
                                >
                                    {this.titleForResignState(this.props.resignState, this.props.strategy)}
                                </Text>
                                {this.props.subtext && <Text type="secondary">{this.props.subtext}</Text>}
                            </Space>
                            <ReleaseActionsComponent
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                releaseId={this.props.releaseId}
                                version={this.props.version}
                                releaseType={this.props.releaseType}
                                releaseStatus={this.props.releaseStatus}
                                editReleaseDisabled={this.props.convertOrDeleteDisabled}
                                deleteReleaseDisabled={this.props.convertOrDeleteDisabled}
                                skipReleaseDisabled={this.props.skipReleaseDisabled}
                                rollbackTargetVersionInfo={this.props.targetVersionInfo}
                                canBeSubmitted={this.props.canBeSubmitted}
                                canRevertCompletedStatus={this.props.canRevertCompletedStatus}
                            />
                        </div>
                        <ReleaseEventsTimelineSection
                            orgId={this.props.orgId}
                            appId={this.props.appId}
                            releaseId={this.props.releaseId}
                            stepRunId={this.props.stepRunComponentProps?.stepRunId}
                            context={ReleaseTimelineSectionContext.ReleaseStep}
                            releaseStepType={this.props.releaseStepType}
                        />
                    </div>
                    <RollbackBuildModule
                        stepRunComponentProps={this.props.stepRunComponentProps}
                        autoAssignBetaGroupsEnabled={this.props.autoAssignBetaGroupsEnabled}
                        autoApplyBetaBuildNotesAutomationIsActive={this.props.autoApplyBetaBuildNotesAutomationIsActive}
                        orgId={this.props.orgId}
                        moduleTitle={'Rollback sequence and build details'}
                        appId={this.props.appId}
                        releaseId={this.props.releaseId}
                        releaseStepType={this.props.releaseStepType}
                        releaseStepStatus={this.props.stepStatus}
                        platform={this.props.platform}
                        appIdentifiers={this.props.appIdentifiers}
                        version={this.props.version}
                        services={this.props.distributionServices}
                        betaTesters={this.props.betaTesters}
                        betaGroups={this.props.betaGroups}
                        stabilityIntegrations={this.props.stabilityIntegrations}
                        buildInfo={this.props.build}
                        enableRetry={this.props.resignState === ResignSequenceProgressState.Failed}
                        betaBuild={this.props.betaBuild}
                        strategy={this.props.strategy}
                        ciBuild={this.props.ciBuild}
                        workflowData={this.props.workflowData}
                        autoUploadBinaryArtifactToAppStoreAutomationId={
                            this.props.autoUploadBinaryArtifactToAppStoreAutomationId
                        }
                        autoUploadDsymsToStabilityMonitoringAutomationId={
                            this.props.autoUploadDsymsToStabilityMonitoringAutomationId
                        }
                        autoDownloadArtifactsAutomationId={this.props.autoDownloadArtifactsAutomationId}
                    />
                    {this.props.sourceSummary && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                minWidth: 0,
                                gap: 18,
                                marginTop: 18,
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ fontSize: 20, color: antGrey.grey7 }}>Source version</Text>
                            </div>
                            <PastReleaseComponent
                                summary={this.props.sourceSummary}
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                platform={this.props.platform}
                                appStoreType={this.props.appStoreType}
                                stabilityIntegration={this.props.stabilityIntegrations?.[0]}
                                appStoreIntegration={this.props.appStore}
                            />
                        </div>
                    )}
                </Content>
                <Footer style={styles.footerStyle}>
                    <RollbackBuildStepFooter
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        releaseId={this.props.releaseId}
                        disabled={this.props.resignState !== ResignSequenceProgressState.NotStarted}
                        strategy={this.props.strategy}
                        stepRunComponentProps={this.props.stepRunComponentProps}
                    />
                </Footer>
            </Layout>
        );
    }
}

const RollbackBuildStepFooter: React.FC<{
    orgId: string;
    appId: string;
    releaseId: string;
    disabled: boolean;
    strategy: RollbackResigningStrategy;
    stepRunComponentProps: ReleaseStepOrStepRunComponentProps['stepRunComponentProps'];
}> = (props) => {
    const dispatch = useDispatch();
    const signingKeys = useSelector((state: RootState) => selectSigningKeys(state, props.appId) ?? []);
    const app = useSelector((state: RootState) => selectApp(state, props.appId));
    const configuredSigningKey = signingKeys.find(
        (signingKey) => signingKey.id === app?.appSettings?.signingKeyIdForResigning,
    );
    const newestSigningKey = React.useMemo(
        () =>
            signingKeys
                .slice()
                .sort((lhs, rhs) => new Date(rhs.updatedAt).getTime() - new Date(lhs.updatedAt).getTime())[0],
        [signingKeys],
    );
    const [selectedSigningKeyId, setSelectedSigningKeyId] = React.useState<string>();
    const effectiveSigningKeyId =
        props.strategy === RollbackResigningStrategy.LocalResign ?
            (selectedSigningKeyId ?? configuredSigningKey?.id ?? newestSigningKey?.id)
        :   undefined;
    const manuallySelectedSigningKeyId =
        props.strategy === RollbackResigningStrategy.LocalResign ? selectedSigningKeyId : undefined;
    const [
        startResignSequenceForStepRun,
        startResignSequenceIsLoadingStepRun,
        startResignSequenceResponseTypeStepRun,
        startResignSequenceErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.StartResignSequence, props.stepRunComponentProps);
    const isLoading = useSelector((state: RootState) =>
        props.stepRunComponentProps ?
            startResignSequenceIsLoadingStepRun
        :   getIsLoading(ReleaseUpdatable.TryStartResignSequence, state, props.releaseId),
    );
    const resignError = useSelector((state: RootState) =>
        props.stepRunComponentProps ?
            startResignSequenceErrorStepRun
        :   getError(ReleaseUpdatable.TryStartResignSequence, state, props.releaseId),
    );
    const resignSucceeded = useSelector((state: RootState) =>
        props.stepRunComponentProps ?
            startResignSequenceResponseTypeStepRun === RunwayResponseType.Success
        :   getUpdated(ReleaseUpdatable.TryStartResignSequence, state, props.releaseId),
    );

    React.useEffect(() => {
        if (resignSucceeded) {
            globalMessage.success('Re-signing sequence started!');
        }
    }, [resignSucceeded]);

    React.useEffect(() => {
        if (resignError) {
            globalMessage.error('Failed to start re-signing sequence');
        }
    }, [resignError]);

    const onClickResign = () => {
        if (props.stepRunComponentProps) {
            startResignSequenceForStepRun({ signingKeyId: manuallySelectedSigningKeyId });
        } else {
            const request = {
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                signingKeyId: manuallySelectedSigningKeyId,
            };
            dispatch(tryUpdateRelease(ReleaseUpdatable.TryStartResignSequence, request, request.releaseId));
        }
    };
    return (
        <Space>
            {props.strategy === RollbackResigningStrategy.LocalResign && (
                <Select
                    style={{ width: 280 }}
                    value={effectiveSigningKeyId}
                    placeholder="Select signing key"
                    disabled={props.disabled}
                    onChange={setSelectedSigningKeyId}
                    options={signingKeys.map((signingKey) => ({
                        value: signingKey.id,
                        label:
                            configuredSigningKey?.id === signingKey.id ?
                                `${signingKey.name} (default signing key)`
                            :   signingKey.name,
                    }))}
                />
            )}
            <PermissionedActionButton
                actionType={RunwayUserAction.UAStartRollbackResigningSequence}
                releaseId={props.releaseId}
                onClick={onClickResign}
                icon={<SafetyCertificateOutlined />}
                loading={isLoading}
                type="primary"
                disabled={
                    props.disabled ||
                    (props.strategy === RollbackResigningStrategy.LocalResign && signingKeys.length === 0)
                }
            >
                Start {props.strategy === RollbackResigningStrategy.CIResign ? 'CI rebuild' : 're-signing sequence'}
            </PermissionedActionButton>
        </Space>
    );
};

StepContentRollbackBuild.contextType = ReleaseStepInactiveContext;

const mapProps = (state: RootState, ownProps: ReleaseStepOrStepRunComponentProps) => {
    const { orgId, appId, releaseId, releaseStepType } = ownProps;

    const release = selectRelease(state, releaseId);
    const app = selectApp(state, appId);
    const releaseStep = selectReleaseStep(state, releaseId, releaseStepType);
    const stepRunId = ownProps.stepRunComponentProps?.stepRunId;
    const stepRun = selectStepRun(state, stepRunId);
    const stepData: ReleaseStepRollbackBuildData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;
    const stepStatus = stepRun ? stepRun.status : releaseStep.status;
    const build = stepData.build;
    const subtext = stepData.subtext;
    const resignState = stepData.resignSequenceState;
    const firstDestination = stepRun?.destinations?.[0];
    const appPlatform = firstDestination?.platform ?? app.appSettings.platform;
    const platform = firstDestination?.platform ?? app.appSettings.platform;
    const appStoreType = firstDestination?.appStoreType ?? app.appSettings.appStoreType;

    const canBeSubmitted = stepData.canBeSubmitted;
    const canRevertCompletedStatus = release.canRevertCompletedStatus;
    const releaseStatus = release.status;

    const targetVersionInfo = selectRollbackTargetVersionInfo(state, releaseId);

    const appStore = selectAppStoreIntegration(state, appId) as AppDistributionService;
    const distributionServices = selectAppDistributionServices(state, appId);
    const stabilityIntegrations = selectStabilityIntegrations(state, appId);
    const appIdentifiers = selectAllAppIdentifiers(state, appId);

    const version = selectRelease(state, releaseId).version;
    const isRunwayOrg = selectIsRunwayOrg(state);

    const sourceSummary = stepData.sourceVersion?.completedReleaseSummary;
    const sourceVersion = stepData.sourceVersion?.version;

    const releaseType = release.type;
    const skipReleaseDisabled = [ReleaseTimelinePhase.Current, ReleaseTimelinePhase.Rollback].includes(
        release.timelinePhase,
    );
    const convertOrDeleteDisabled =
        release.status === ReleaseStatus.Skipped || release.status === ReleaseStatus.Completed;

    const betaGroups = stepData.betaGroups;
    const betaTesters = stepData.betaTesters;
    const betaBuild = stepData.betaBuild;

    const automationRuns = stepRun?.automationRuns ?? releaseStep?.automationRuns;

    const autoAssignBetaGroupsAutomationId = getAutomationIdByType(automationRuns, AutomationType.AutoAssignBetaBuilds);

    const autoAssignBetaGroupsAutomation = selectAutomationById(state, appId, autoAssignBetaGroupsAutomationId);

    const autoAssignBetaGroupsEnabled = autoAssignBetaGroupsAutomation?.enabled;

    const autoApplyBetaBuildNotesAutomation = automationRuns?.find(
        (automationRun) => automationRun.type === AutomationType.AutoApplyBetaBuildNotes,
    );

    const autoApplyBetaBuildNotesAutomationIsActive =
        autoApplyBetaBuildNotesAutomation ? automationIsActive(autoApplyBetaBuildNotesAutomation) : false;

    const strategy = stepData.strategy;
    const ciBuild = stepData.ciBuild;
    const workflowData = stepData.workflowData;

    const autoUploadDsymsToStabilityMonitoringAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadDsymsToStabilityMonitoring,
    );

    const autoUploadBinaryArtifactToAppStoreAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadBinaryArtifactToAppStore,
    );

    const autoDownloadArtifactsAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoDownloadArtifacts,
    );

    return {
        orgId,
        appId,
        releaseId,
        autoApplyBetaBuildNotesAutomationIsActive,
        autoAssignBetaGroupsEnabled,
        build,
        releaseStepType,
        appPlatform,
        canBeSubmitted,
        canRevertCompletedStatus,
        releaseStatus,
        stepStatus,
        platform,
        appStore,
        distributionServices,
        stabilityIntegrations,
        stepRunComponentProps: ownProps.stepRunComponentProps,
        appIdentifiers,
        appStoreType,
        version,
        isRunwayOrg,
        sourceSummary,
        sourceVersion,
        subtext,
        releaseType,
        skipReleaseDisabled,
        convertOrDeleteDisabled,
        resignState,
        betaGroups,
        betaTesters,
        betaBuild,
        targetVersionInfo,
        strategy,
        ciBuild,
        workflowData,
        autoUploadBinaryArtifactToAppStoreAutomationId,
        autoUploadDsymsToStabilityMonitoringAutomationId,
        autoDownloadArtifactsAutomationId,
    };
};

const mapDispatch = () => {
    return {};
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(StepContentRollbackBuild);
